// decision-auto-boundary-tokens.ts — V2-1 (spec V2 §4.1 luật auto điều 3 + 4).
//
// Tập token ranh giới mà controller tự so (không tin `boundary_claims`):
//  - `resource_names` (control root, do người khai lúc init): `<loại>:<định
//    danh>` cho 2 token — chuỗi đầy đủ + `<định danh>`;
//  - tên key env: `env.example`/`.env.example` + `env_allowlist` của profile
//    verification — HỢP bản live, HEAD và tập chụp lúc khởi động run (review
//    M2, decision-auto-env-key-sources.ts) — và recipe release (config);
//  - tên key nguồn secrets (Codex F8, decision-auto-secret-key-name-tokens.ts):
//    `test.env` (loader `ok`) + map stdin release, hợp mọi key đã thấy trong
//    run; `test.env` tồn tại mà không kiểm chứng được ⇒ `problem`;
//  - base_ref: `repo.base_branch` init lưu ở control root (mission-base-branch.ts,
//    review M3) HỢP `release.target_branch`; mission v1 ⇒ chỉ target_branch;
//  - approver: `user_id` + `username` của `mattermost.approvers` và
//    `actor_binding.os_account`.
// Phần từ CONFIG lấy từ effective config TRONG BỘ NHỚ của run (`startSupervisor`
// nạp một lần, §7) — không đọc lại file giữa run (doctor/resolve-deploy ghi
// lại snapshot `effective-config*.json`).
// Nguồn ranh giới không đọc được (resource_names/actor binding vắng/hỏng) ⇒
// `problem` ⇒ caller về `ask`: ranh giới không biết không phải giấy phép.
import { readEnvKeyNamesLiveAndHead } from "./decision-auto-env-key-sources.js";
import { readSecretKeyNameTokens } from "./decision-auto-secret-key-name-tokens.js";
import { readMissionActorBinding } from "./mission-actor-binding.js";
import { readMissionBaseBranch } from "./mission-base-branch.js";
import { readMissionDeclaredResources } from "./mission-declared-resources.js";
import { missionIdFromControlRoot } from "./mission-secrets-test-env-loader.js";
import type { AutopilotConfig } from "./schemas/config-schema.js";
import type { AutopilotConfigV2 } from "./schemas/config-v2-schema.js";

/** Phần cấu hình của luật auto, chụp MỘT lần lúc khởi động run. */
export interface DecisionAutoRunConfig {
  maxAutoDecisionsPerMission: number;
  configTokens: readonly string[];
}

/**
 * Dựng MỘT lần lúc khởi động run (`runMissionToIdle`): trần + token từ config
 * trong bộ nhớ + tập key env chụp từ worktree lúc đó (review M2 — worker xoá
 * key giữa run không làm mất token).
 */
export async function buildDecisionAutoRunConfig(
  config: Readonly<AutopilotConfig>,
  configV2: Readonly<AutopilotConfigV2>,
  repoDir: string,
  missionId?: string,
): Promise<DecisionAutoRunConfig> {
  const release = config.release;
  // Codex F8: chụp tên key secrets lúc khởi động (registry giữ hợp cả run);
  // nguồn hỏng ở đây đã bị preflight `run` chặn, mỗi lần xét auto đọc lại.
  await readSecretKeyNameTokens(missionId);
  return Object.freeze({
    maxAutoDecisionsPerMission: configV2.max_auto_decisions_per_mission,
    configTokens: Object.freeze([
      ...config.mattermost.approvers.flatMap((a) => [a.user_id, a.username]),
      ...(release !== undefined ? [release.target_branch] : []),
      ...(release?.deploy_recipe?.env_allowlist ?? []),
      ...(release?.verify_recipe?.env_allowlist ?? []),
      ...(await readEnvKeyNamesLiveAndHead(repoDir)),
    ]),
  });
}

/** `db:orders` ⇒ ["db:orders", "orders"]; không có `:` ⇒ chỉ chuỗi đầy đủ. */
function resourceTokens(name: string): string[] {
  const at = name.indexOf(":");
  return at > 0 ? [name, name.slice(at + 1)] : [name];
}

export type BoundaryTokensRead = { ok: true; tokens: string[] } | { ok: false; problem: string };

export async function collectDecisionBoundaryTokens(
  controlRoot: string,
  repoDir: string,
  runConfig: DecisionAutoRunConfig,
): Promise<BoundaryTokensRead> {
  const resources = await readMissionDeclaredResources(controlRoot);
  if (resources === undefined) return { ok: false, problem: "không đọc được resource_names" };
  const actor = await readMissionActorBinding(controlRoot);
  if (actor === undefined) return { ok: false, problem: "không đọc được actor_binding" };
  const secretKeys = await readSecretKeyNameTokens(missionIdFromControlRoot(controlRoot));
  if (!secretKeys.ok) return secretKeys;
  const all = [
    ...secretKeys.tokens,
    ...resources.flatMap(resourceTokens),
    ...(await readEnvKeyNamesLiveAndHead(repoDir)),
    ...runConfig.configTokens,
    // M3: base_ref do người khai lúc init (mission V2); v1 ⇒ chỉ target_branch.
    (await readMissionBaseBranch(controlRoot)) ?? "", // "" bị lọc dưới
    actor.os_account,
  ];
  // Token rỗng bị bỏ (spec §4.1).
  return { ok: true, tokens: [...new Set(all.map((t) => t.trim()).filter((t) => t.length > 0))] };
}
